import { FC, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Form } from 'react-final-form';

import { useGetEventPagesForReportQuery } from 'services/eventPagesForReportApi';
import { useAppSelector } from 'helpers/hooks';
import { getActiveRole } from 'selectors/admins';
import { SelectField } from 'components/Forms/SelectField';
import Loader from 'components/Loader';
import { DashboardScreen, trackReportExported } from 'helpers/mixpanelEvents';
import { hasInvalidClientId } from './utils';

interface EventTicketReportOptionsProps {
  clientId: number;
  exportButtonEnabled: boolean;
  runReport: (eventSlug: string) => void;
  screen: DashboardScreen;
}

export const EventTicketReportOptions: FC<EventTicketReportOptionsProps> = ({
  clientId,
  exportButtonEnabled,
  runReport,
  screen,
}) => {
  const [selectedEventSlug, setSelectedEventSlug] = useState<string>('');
  const activeRole = useAppSelector(getActiveRole);

  const {
    data: eventPagesData,
    isLoading: isLoadingEvents,
    isError: isEventsError,
  } = useGetEventPagesForReportQuery({ client_id: clientId }, { skip: !clientId || !activeRole });

  const eventGroups = useMemo(() => {
    if (!eventPagesData?.pages) return [];

    const todayStart = dayjs().startOf('day');
    const upcoming = eventPagesData.pages
      .filter(e => !dayjs(e.start_datetime).isBefore(todayStart))
      .sort((a, b) => dayjs(a.start_datetime).valueOf() - dayjs(b.start_datetime).valueOf());
    const past = eventPagesData.pages
      .filter(e => dayjs(e.start_datetime).isBefore(todayStart))
      .sort((a, b) => dayjs(b.start_datetime).valueOf() - dayjs(a.start_datetime).valueOf());

    const toOption = (event: (typeof eventPagesData.pages)[number]) => ({
      value: event.query_str,
      label: `${event.value} (${dayjs(event.start_datetime).format('MMM D, YYYY')})`,
    });

    const groups: { label: string; options: { value: string; label: string }[] }[] = [];
    if (upcoming.length > 0) {
      groups.push({ label: 'UPCOMING EVENTS', options: upcoming.map(toOption) });
    }
    if (past.length > 0) {
      groups.push({ label: 'PAST EVENTS', options: past.map(toOption) });
    }
    return groups;
  }, [eventPagesData]);

  const handleExport = () => {
    if (hasInvalidClientId(clientId) || !selectedEventSlug) return;
    trackReportExported({ screen, report_type: 'event', event_slug: selectedEventSlug });
    runReport(selectedEventSlug);
  };

  return (
    <Form
      initialValues={{ event_page_slug: '' }}
      onSubmit={handleExport}
      render={({ handleSubmit }) => (
        <form onSubmit={handleSubmit} noValidate>
          <div className="card report-options-card">
            <div className="card-header report-options-head">
              <div className="report-options-subhead">
                <span className="h4-text">Event Report</span>
                <span className="body-text">
                  This report provides all event ticket and non-ticket donations for a specific
                  event.
                </span>
              </div>
              <button
                type="submit"
                className="btn-inkBlue btn btn-secondary report-options-export-button"
                disabled={
                  hasInvalidClientId(clientId) || !selectedEventSlug || !exportButtonEnabled
                }
              >
                Export
              </button>
            </div>
            <div className="card-body">
              {isLoadingEvents && <Loader type="dots" />}
              {isEventsError && (
                <div className="text-danger">Failed to load events. Please try again.</div>
              )}
              {!isLoadingEvents && !isEventsError && (
                <div className="report-options-event-selector">
                  <label>Filter By</label>
                  <SelectField
                    label={
                      <span className="report-options-event-placeholder-text">Event Page</span>
                    }
                    name="event_page_slug"
                    groups={eventGroups}
                    onChange={(value: string | number) => setSelectedEventSlug(String(value))}
                  />
                </div>
              )}
            </div>
          </div>
        </form>
      )}
    />
  );
};

export default EventTicketReportOptions;
